import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Modal, Box, Button, Typography, Tabs, Tab, Tooltip, IconButton } from '@mui/material';
import { Close, Email, People, ArrowForward, Send } from '@mui/icons-material';
import { useSendEmail, type EmailStatus } from '../hooks/useSendEmail';
import type { SendEmailModalProps } from '../hooks/useSendEmailModal';
import { EmailDetails } from './EmailDetails';
import { LoadingButton } from '@mui/lab';
import { CustomdateRangePicker } from './CustomdateRangePicker';
import EmailResponseSummary from './EmailResponseSummary';
import EmailTemplatePreview from './EmailTemplatePreview';
import EmailSendingModal from './EmailSendingModal';
import { showWarningSnackbar, showSuccessSnackbar } from '../../../components/snackBar/snakbar';
import { DateRangeRsuite } from '../../../components';
import dayjs from 'dayjs';
import { formatUnixTimestamp } from '../../../library';
import { TemplateSaveOption } from './TemplateUpdateConfirmationDialog';

export const SendEmailModalWrapper: React.FC<SendEmailModalProps> = ({
  open,
  onClose,
  initialPageType = '',
  recipients = [],
  onUpdateEmail,
  isEditRecipient,
  setIsEditRecipient,
  removeRecipient,
}) => {
  
  const getTemplateIdByPageType = (pageType: string): number => {
    const pageTypeLower = pageType.toLowerCase();
    
    if (pageTypeLower.includes('payment') || pageTypeLower.includes('payment confirmation')) {
      return 4;
    } else if (pageTypeLower.includes('purchase')) {
      return 3;
    } else if (pageTypeLower.includes('invoice') || pageTypeLower.includes('sales')) {
      return 1;
    } else if (pageTypeLower.includes('reminder')) {
      return 2;
    } else if (pageTypeLower.includes('debit')) {
      return 5;
    } else if (pageTypeLower.includes('credit')) {
      return 6;
    } else if (pageTypeLower.includes('receipt')) {
      return 2;
    } else if (pageTypeLower.includes('customer')) {
      return 7;
    } else if (pageTypeLower.includes('supplier')) {
      return 7;
    } else if (pageTypeLower.includes('pos')) {
      return 1;
    }
    
    return 1;
  };
  const [activeTab, setActiveTab] = useState(0);
  const [isTemplateSaved, setIsTemplateSaved] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [getCurrentTemplateContent, setGetCurrentTemplateContent] = useState<(() => string) | null>(null);
  const [saveTemplateFunction, setSaveTemplateFunction] = useState<(() => Promise<void>) | null>(null);
  const [showUnsavedChangesPopup, setShowUnsavedChangesPopup] = useState<(() => boolean) | null>(null);
  const [currentTemplateContent, setCurrentTemplateContent] = useState<string>('');
  const [templateSaveOption, setTemplateSaveOption] = useState<TemplateSaveOption | null>(null);
  const templateContentRef = useRef<string>('');
  const [resetTemplatePreview, setResetTemplatePreview] = useState(false);
  const [customContentForThisEmail, setCustomContentForThisEmail] = useState<string | null>(null);
  const [emailSendingProgress, setEmailSendingProgress] = useState({
    sentEmails: 0,
    totalEmails: 0,
    message: 'Preparing to send emails...',
    progressPercentage: 0,
  });

  const {
    loading,
    pageType,
    dateRange,
    handleDateRangeChange,
    modalHeader,
    handleSendEmail: originalHandleSendEmail,
    handleCloseResponseSummary,
    responseSummary,
    startdate,
    enddate,
    handleCloseWrapperModal: originalHandleCloseWrapperModal,
  } = useSendEmail({
    initialPageType,
    recipients,
    onClose,
    onProgress: (progress) => {
      setEmailSendingProgress(progress);
    },
    getTemplateContent: () => {
      if (getCurrentTemplateContent) {
        return getCurrentTemplateContent();
      }
      return templateContentRef.current;
    },
  });

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  // Reset template preview when modal opens
  useEffect(() => {
    if (open) {
      setResetTemplatePreview(false);
      setActiveTab(0);
      setTemplateSaveOption(null);
      setCurrentTemplateContent('');
      setCustomContentForThisEmail(null);
      templateContentRef.current = '';
      // Reset progress state when modal opens
      setEmailSendingProgress({
        sentEmails: 0,
        totalEmails: 0,
        message: 'Preparing to send emails...',
        progressPercentage: 0,
      });
    }
  }, [open]);

  const handleCloseWrapperModal = useCallback(() => {
    // Reset template preview state when modal closes
    setResetTemplatePreview(true);
    setCustomContentForThisEmail(null);
    setTimeout(() => {
      setResetTemplatePreview(false);
    }, 100);
    originalHandleCloseWrapperModal();
  }, [originalHandleCloseWrapperModal]);

  const handleSaveTemplate = (templateData: any) => {
    // Handle template save if needed
  };

  const handleTemplateSaveStatusChange = (saved: boolean) => {
    setIsTemplateSaved(saved);
  };

  const handleUnsavedChangesChange = (hasChanges: boolean) => {
    setHasUnsavedChanges(hasChanges);
  };

  const handleGetSaveFunction = (saveFunction: () => Promise<void>) => {
    setSaveTemplateFunction(() => saveFunction);
  };

  const handleGetShowUnsavedChangesPopup = (showFunction: () => boolean) => {
    setShowUnsavedChangesPopup(() => showFunction);
  };

  const handleGetCurrentTemplateContent = (getContent: () => string) => {
    setGetCurrentTemplateContent(() => getContent);
    // Update current template content whenever it changes
    const content = getContent();
    setCurrentTemplateContent(content);
    templateContentRef.current = content;
  };

  const handleConfirmTemplateUpdate = (option: TemplateSaveOption) => {
    setTemplateSaveOption(option);
    // Update template content based on the option
    if (getCurrentTemplateContent) {
      const content = getCurrentTemplateContent();
      setCurrentTemplateContent(content);
      // If "This Email Only" is selected, store the custom content in parent state
      if (option === 'this_email_only') {
        setCustomContentForThisEmail(content);
      }
    }
    setActiveTab(1);
  };

  const handleCancelTemplateUpdate = () => {
    showWarningSnackbar('Template changes have been discarded. The original template content will be used.');
  };

  const handleNextClick = async () => {
    if (hasUnsavedChanges && showUnsavedChangesPopup) {
      const popupShown = showUnsavedChangesPopup();
      if (popupShown) {
        return;
      }
    }
    
    // Get current template content before proceeding
    if (getCurrentTemplateContent) {
      const content = getCurrentTemplateContent();
      setCurrentTemplateContent(content);
    }
    
    setActiveTab(1);
  };

  const handleSendClick = async () => {
    if (hasUnsavedChanges && showUnsavedChangesPopup) {
      const popupShown = showUnsavedChangesPopup();
      if (popupShown) {
        return;
      }
    }
    
    // Get current template content before sending and update ref
    if (getCurrentTemplateContent) {
      const content = getCurrentTemplateContent();
      setCurrentTemplateContent(content);
      templateContentRef.current = content;
    }
    
    // Reset progress state
    setEmailSendingProgress({
      sentEmails: 0,
      totalEmails: recipients.length,
      message: 'Preparing to send emails...',
      progressPercentage: 0,
    });
    
    // Create a new hook instance with updated template content
    // Since hooks can't be recreated, we'll need to pass content via ref
    // The hook will read from templateContentRef.current
    originalHandleSendEmail();
  };

  




  return (
    <>
      <Modal 
        open={open} 
        aria-labelledby="send-email-modal"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: { 
              xs: '98vw', 
              sm: '95vw', 
              md: '90vw', 
              lg: '80vw',
              xl: '70vw'
            },
            maxWidth: { xs: 'none', sm: 1000, md: 1200, lg: 1400 },
            height: {
              xs: '98vh',
              sm: '95vh',
              md: '92vh',
              lg: '88vh'
            },
            bgcolor: 'background.paper',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            borderRadius: { xs: 2, sm: 3 },
            display: 'flex',
            flexDirection: 'column',
            p: 0,
            overflow: 'hidden',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          }}
        >
          {/* Modern Header with Gradient */}
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 3,
              background: 'linear-gradient(135deg, #911B7C 0%, #6B1460 100%)',
              color: 'white',
              p: { xs: 2, sm: 2.5 },
              boxShadow: '0 4px 20px rgba(145, 27, 124, 0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '12px',
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backdropFilter: 'blur(10px)',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                }}
              >
                <Email sx={{ fontSize: 28 }} />
              </Box>
              <Box>
                <Typography 
                  component="h2" 
                  id="send-email-modal" 
                  sx={{ 
                    m: 0, 
                    fontSize: { xs: '16px', sm: '18px', md: '20px' },
                    fontWeight: 700,
                    mb: 0.25,
                  }}
                >
                  {modalHeader}
                </Typography>
                <Typography 
                  sx={{ 
                    fontSize: { xs: '11px', sm: '12px' },
                    opacity: 0.9,
                    fontWeight: 400,
                  }}
                >
                  Manage email template and recipients
                </Typography>
              </Box>
            </Box>
            <IconButton
              onClick={handleCloseWrapperModal}
              size="small"
              sx={{
                color: 'white',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                },
              }}
            >
              <Close />
            </IconButton>
          </Box>

          {/* Modern Tabs */}
          <Box 
            sx={{ 
              borderBottom: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
              position: 'sticky',
              top: { xs: 88, sm: 104 },
              zIndex: 2,
            }}
          >
            <Tabs 
              value={activeTab} 
              onChange={handleTabChange} 
              aria-label="email modal tabs"
              variant={window.innerWidth < 600 ? 'scrollable' : 'standard'}
              scrollButtons="auto"
              sx={{
                '& .MuiTabs-indicator': {
                  height: 3,
                  borderRadius: '3px 3px 0 0',
                  background: 'linear-gradient(90deg, #911B7C 0%, #6B1460 100%)',
                },
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: { xs: '13px', sm: '14px' },
                  minHeight: { xs: 48, sm: 56 },
                  px: { xs: 2, sm: 3 },
                  color: 'text.secondary',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    color: '#911B7C',
                    backgroundColor: 'rgba(145, 27, 124, 0.04)',
                  },
                  '&.Mui-selected': {
                    color: '#911B7C',
                    fontWeight: 700,
                  },
                },
              }}
            >
              <Tab 
                icon={<Email sx={{ fontSize: 18 }} />}
                iconPosition="start"
                label="Email Template"
                sx={{
                  gap: 1,
                }}
              />
              <Tab 
                icon={<People sx={{ fontSize: 18 }} />}
                iconPosition="start"
                label="Recipients"
                sx={{
                  gap: 1,
                }}
              />
            </Tabs>
          </Box>

          {/* Sticky Date Range */}
          {(pageType === 'customer' || pageType === 'supplier') && activeTab === 1 && (
            <Box
              sx={{
                position: 'sticky',
                top: { xs: 144, sm: 160 },
                zIndex: 2,
                bgcolor: 'background.paper',
                px: { xs: 2, sm: 3 },
                py: { xs: 1.5, sm: 2 },
                borderBottom: '1px solid',
                borderColor: 'divider',
                background: 'linear-gradient(to bottom, rgba(145, 27, 124, 0.02), transparent)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <Box
                    sx={{
                      width: 4,
                      height: 16,
                      borderRadius: '2px',
                      background: 'linear-gradient(135deg, #911B7C 0%, #6B1460 100%)',
                    }}
                  />
                <Typography 
                  variant="subtitle2" 
                  sx={{ 
                    fontSize: { xs: '13px', sm: '15px' },
                    fontWeight: 600,
                    color: 'text.primary',
                  }}
                >
                  Select Date Range
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                <DateRangeRsuite value={[dateRange.from, dateRange.to]} onChange={handleDateRangeChange} showHeader  />
                {dateRange.from && dateRange.to && (
                  <Box
                    sx={{
                      px: 2,
                      py: 1,
                      borderRadius: 2,
                    bgcolor: 'rgba(145, 27, 124, 0.1)',
                    color: '#911B7C',
                      fontSize: '13px',
                      fontWeight: 500,
                    }}
                  >
                    {formatUnixTimestamp(dateRange.from)} to {formatUnixTimestamp(dateRange.to)}
                  </Box>
                )}
              </Box>
            </Box>
          )}
          
          {/* Tab Content */}
          <Box sx={{ 
            flex: 1, 
            overflow: 'auto', 
            p: activeTab === 0 ? { xs: 0.5, sm: 0.75 } : 0,
            minHeight: 0,
          }}>
            {activeTab === 0 && (
              <EmailTemplatePreview
                templateId={getTemplateIdByPageType(initialPageType)}
                onSaveTemplate={handleSaveTemplate}
                onTemplateSaveStatusChange={handleTemplateSaveStatusChange}
                onUnsavedChangesChange={handleUnsavedChangesChange}
                onGetCurrentTemplateContent={handleGetCurrentTemplateContent}
                onGetSaveFunction={handleGetSaveFunction}
                onGetShowUnsavedChangesPopup={handleGetShowUnsavedChangesPopup}
                onConfirmTemplateUpdate={handleConfirmTemplateUpdate}
                onCancelTemplateUpdate={handleCancelTemplateUpdate}
                resetOnClose={resetTemplatePreview}
                customContentForThisEmail={customContentForThisEmail}
                onSetCustomContentForThisEmail={setCustomContentForThisEmail}
              />
            )}
            
            {activeTab === 1 && (
              <Box sx={{ py: 0.2 }}>
                <EmailDetails
                  recipients={recipients}
                  onUpdateEmail={onUpdateEmail}
                  setIsEditRecipient={setIsEditRecipient}
                  removeRecipient={removeRecipient}
                  addVoucherCol={pageType !== 'customer' && pageType !== 'supplier'}
                />
              </Box>
            )}
          </Box>

          {/* Modern Sticky Actions Footer */}
          <Box
            sx={{
              position: 'sticky',
              bottom: 0,
              zIndex: 3,
              bgcolor: 'background.paper',
              borderTop: '1px solid',
              borderColor: 'divider',
              background: 'linear-gradient(to top, rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0.98))',
              backdropFilter: 'blur(10px)',
              py: { xs: 2, sm: 2.5 },
              px: { xs: 2, sm: 3 },
              display: 'flex',
              justifyContent: 'flex-end',
              gap: { xs: 1.5, sm: 2 },
              flexDirection: { xs: 'column', sm: 'row' },
              boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.05)',
            }}
          >
            {activeTab === 0 ? (
              <>
                <Button 
                  variant="outlined" 
                  onClick={handleCloseWrapperModal}
                  startIcon={<Close />}
                  sx={{ 
                    minWidth: { xs: '100%', sm: 120 },
                    order: { xs: 2, sm: 1 },
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '14px',
                    py: 1.25,
                    borderRadius: 2,
                    borderColor: 'divider',
                    '&:hover': {
                      borderColor: '#911B7C',
                      bgcolor: 'rgba(145, 27, 124, 0.04)',
                    },
                  }}
                >
                  Close
                </Button>
                <Tooltip 
                  title={hasUnsavedChanges ? "You have unsaved changes. They will be saved automatically when you proceed." : ""} 
                  arrow
                >
                  <Button 
                    variant="contained" 
                    onClick={handleNextClick}
                    endIcon={<ArrowForward />}
                    sx={{ 
                      minWidth: { xs: '100%', sm: 140 },
                      order: { xs: 1, sm: 2 },
                      position: 'relative',
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '14px',
                      py: 1.25,
                      borderRadius: 2,
                    background: 'linear-gradient(135deg, #911B7C 0%, #6B1460 100%)',
                    boxShadow: '0 4px 15px rgba(145, 27, 124, 0.4)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #6B1460 0%, #911B7C 100%)',
                      boxShadow: '0 6px 20px rgba(145, 27, 124, 0.5)',
                        transform: 'translateY(-1px)',
                      },
                      '&:active': {
                        transform: 'translateY(0)',
                      },
                    }}
                  >
                    Next
                    {hasUnsavedChanges && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: -4,
                          right: -4,
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          backgroundColor: 'warning.main',
                          border: '2px solid white',
                          boxShadow: '0 2px 8px rgba(255, 152, 0, 0.4)',
                        }}
                      />
                    )}
                  </Button>
                </Tooltip>
              </>
            ) : (
              <>
                <Button 
                  variant="outlined" 
                  onClick={handleCloseWrapperModal}
                  startIcon={<Close />}
                  sx={{ 
                    minWidth: { xs: '100%', sm: 120 },
                    order: { xs: 2, sm: 1 },
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '14px',
                    py: 1.25,
                    borderRadius: 2,
                    borderColor: 'divider',
                    '&:hover': {
                      borderColor: '#911B7C',
                      bgcolor: 'rgba(145, 27, 124, 0.04)',
                    },
                  }}
                >
                  Close
                </Button>
                <Tooltip 
                  title={hasUnsavedChanges ? "You have unsaved changes. They will be saved automatically when you send." : ""} 
                  arrow
                >
                  <LoadingButton
                    variant="contained"
                    onClick={handleSendClick}
                    loading={loading}
                    disabled={isEditRecipient !== null}
                    startIcon={<Send />}
                    sx={{ 
                      minWidth: { xs: '100%', sm: 160 },
                      order: { xs: 1, sm: 2 },
                      position: 'relative',
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '14px',
                      py: 1.25,
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)',
                      boxShadow: '0 4px 15px rgba(46, 125, 50, 0.4)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #388e3c 0%, #2e7d32 100%)',
                        boxShadow: '0 6px 20px rgba(46, 125, 50, 0.5)',
                        transform: 'translateY(-1px)',
                      },
                      '&:active': {
                        transform: 'translateY(0)',
                      },
                      '&:disabled': {
                        background: 'grey.400',
                        boxShadow: 'none',
                      },
                    }}
                  >
                    Send Emails
                    {hasUnsavedChanges && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: -4,
                          right: -4,
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          backgroundColor: 'warning.main',
                          border: '2px solid white',
                          boxShadow: '0 2px 8px rgba(255, 152, 0, 0.4)',
                        }}
                      />
                    )}
                  </LoadingButton>
                </Tooltip>
              </>
            )}
          </Box>
        </Box>
      </Modal>
      {responseSummary?.open && (
        <EmailResponseSummary
          open={responseSummary?.open}
          onClose={() => handleCloseResponseSummary()}
          data={responseSummary?.data?.data || null}
          addVoucherCol={pageType !== 'customer' && pageType !== 'supplier'}
        />
      )}
      
      {/* Email Sending Loading Modal */}
      <EmailSendingModal
        open={loading}
        progress={emailSendingProgress.progressPercentage}
        message={emailSendingProgress.message}
        totalEmails={emailSendingProgress.totalEmails}
        sentEmails={emailSendingProgress.sentEmails}
      />
    </>
  );
};

export default SendEmailModalWrapper;
